import debug from 'debug'
import CSREntry from 'src/clientRoutes/_app.model.$modelIdSlug.($versionId).csr'
import FullSSREntry from 'src/clientRoutes/_app.model.$modelIdSlug.($versionId).full-ssr'
import { FeatureFlags } from 'src/experiments/featureFlags'
import { dynamicWithFeatureFlag } from 'src/experiments/featureFlags/dynamicWithFeatureFlag'
import { getI18n } from 'src/i18n/i18n.middleware'
import { SSR_DUAL_MODE_EXPERIMENT } from 'src/utils/router/ssrShell'
import { createHydratingClientLoader, dynamic } from 'src/utils/ssr'
import {
  createSsrLoaderErrorData,
  type SsrLoaderErrorReason,
} from 'src/utils/ssr-loader-error'
import type { SSRModel } from 'src/utils/ssr-meta/graphql/model.server'
import {
  entityMeta,
  entityMetaLoader,
  type JsonLdObject,
} from 'src/utils/ssr-meta/seo'
import { isModelPrivate } from '~/utils/generationModel/modelPermissions'
import type { Route } from './+types/_app.model.$modelIdSlug.($versionId)'

const getClientModule = () =>
  import('src/clientRoutes/_app.model.$modelIdSlug.($versionId)')
const log = debug('pixai:ssr-meta')

export const handle = {
  name: 'model-detail',
  track: { scene: 'model-detail' },
  showAppNav: true,
  ssrShell: true,
  // Gate the SSR-shell branch behind the global Stage 2 dual-mode
  // experiment so the route can serve both the legacy CSR path
  // (control) and the Full SSR path (treatment) from the same code
  // base. See `src/utils/router/ssrShell.ts`.
  ssrShellExperiment: SSR_DUAL_MODE_EXPERIMENT,
}

function createModelLoaderErrorResult(
  requestUrl: string,
  reason: SsrLoaderErrorReason,
) {
  return {
    ...entityMetaLoader({ requestUrl }),
    modelApollo: undefined,
    modelShell: undefined,
    ssrLoadError: createSsrLoaderErrorData('model', reason),
  }
}

/**
 * "Soft" failure result — we know the SSR fetch couldn't materialise a
 * model, but we *don't* know why for sure. The full-SSR entry will
 * mount a bare route-scoped Apollo client and let the client-side
 * `<ModelDetailPage>` re-fetch with the user's credentials cookie. If
 * the caller is the owner of a private / training model, that
 * re-fetch succeeds and the page renders normally; if the model
 * really is gone, the client-side `useGetGenerationModel` returns
 * `null` and `<ModelDetailPage>` shows its own not-found state.
 *
 * This exists because anonymous server-side GraphQL (the only auth
 * surface available to `serverGraphqlFetch`) cannot see models that
 * are private, archived, or still training. Treating those `null`
 * responses as fatal SSR errors caused logged-in owners hitting their
 * own training-in-progress LoRA pages to see "Looks like the page
 * decided to take a nap…" instead of the model — see the regression
 * report attached to commit history for `2011612645278020840`
 * (2026-05-14).
 */
function createModelLoaderSoftFailureResult(requestUrl: string) {
  return {
    ...entityMetaLoader({ requestUrl }),
    modelApollo: undefined,
    modelShell: undefined,
  }
}

async function reportModelLoaderError(args: {
  reason: SsrLoaderErrorReason
  requestUrl: string
  entityId?: string | null
  routeParams?: Record<string, unknown>
  error?: unknown
}) {
  const { reportSsrLoaderError } =
    await import('src/utils/ssr-loader-error.server')
  reportSsrLoaderError({
    entity: 'model',
    ...args,
  })
}

function getImageUrl(media?: SSRModel['media']): string | undefined {
  if (!media) return undefined
  if (media.thumbnailUrl) return media.thumbnailUrl
  return media.urls?.[0]?.url ?? undefined
}

/**
 * Mirror `getPublicUrl` from `src/utils/media.ts` (which is what
 * `<ModelDetailImage>` actually feeds to `<SingleImageViewer
 * originalUrl=>`). Used to drive the route-meta image preload so the
 * `<link rel="preload">` href matches the eventual `<img src>` and the
 * browser does not fetch the cover twice.
 */
function getPublicUrlFromMedia(media?: SSRModel['media']): string | undefined {
  return media?.urls?.find(u => u?.variant === 'PUBLIC')?.url ?? undefined
}

function getModelDescription(model: SSRModel): string | undefined {
  const extra = model.extra as { description?: string } | null
  return extra?.description ?? undefined
}

/**
 * Build schema.org JSON-LD for a model detail page. Mirrors the legacy
 * body-level `<ModelDetailJsonLD>` but is emitted from route `meta()` so the
 * `<script type="application/ld+json">` tag lands in `<head>` from the first
 * SSR flush.
 *
 * Returned shape: SoftwareApplication (with optional AggregateRating).
 */
function buildModelJsonLd(params: {
  model: SSRModel
  imageUrl: string
  canonicalUrl: string
  description?: string
  authorName?: string
  tags: string[]
}): JsonLdObject {
  const { model, imageUrl, canonicalUrl, description, authorName, tags } =
    params
  const ratingValue = model.reviewSummary?.score
  const ratingCount = model.reviewSummary?.ratingDetail.reduce(
    (total, { count }) => total + count,
    0,
  )

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: model.title || 'PixAI Model',
    description,
    url: canonicalUrl,
    image: imageUrl,
    applicationCategory: 'Multimedia',
    applicationSubCategory: 'Stable Diffusion Model',
    keywords: tags.length > 0 ? tags.join(', ') : undefined,
    author: authorName
      ? {
          '@type': 'Person',
          name: authorName,
        }
      : undefined,
    aggregateRating:
      ratingValue && ratingCount
        ? {
            '@type': 'AggregateRating',
            ratingValue,
            ratingCount,
          }
        : undefined,
  }
}

const DEFAULT_IMAGE =
  'https://images-ng.pixai.art/images/thumb/428f8ec9-c23a-42c9-a2d7-2de2a10e3bc5'

/**
 * SSR loader for model detail page.
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/ssr/router.ts#L170-L236
 */
export async function loader({ params, request, context }: Route.LoaderArgs) {
  const { attemptParseId, parseIdSlug } =
    await import('src/utils/ssr-meta/url.server')
  const { fetchModelForSSR, fetchModelVersionForSSR } =
    await import('src/utils/ssr-meta/graphql/model.server')
  const { getModelMetaTitleI18n, getModelMetaDescriptionI18n } =
    await import('src/utils/ssr-meta/seo.server')

  const modelIdSlug = params.modelIdSlug
  if (!modelIdSlug) {
    log('model loader missing modelIdSlug url=%s', request.url)
    return createModelLoaderErrorResult(request.url, 'missing-route-param')
  }

  const id = parseIdSlug(modelIdSlug)
  if (!id) {
    log('model loader invalid modelIdSlug=%s url=%s', modelIdSlug, request.url)
    return createModelLoaderErrorResult(request.url, 'invalid-route-param')
  }

  // Parse version ID if provided
  const versionId = params.versionId ? attemptParseId(params.versionId) : null

  // Fetch model and optionally version in parallel
  let model: Awaited<ReturnType<typeof fetchModelForSSR>>
  let modelVersion: Awaited<ReturnType<typeof fetchModelVersionForSSR>>
  try {
    ;[model, modelVersion] = await Promise.all([
      fetchModelForSSR(id),
      versionId ? fetchModelVersionForSSR(versionId) : Promise.resolve(null),
    ])
  } catch (error) {
    // Soft failure: the SSR-side fetch chain failed (network blip,
    // upstream 5xx, etc.). Don't fail the page — let the client-side
    // Apollo client re-fetch with the user's credentials. See
    // `createModelLoaderSoftFailureResult` for the full rationale.
    await reportModelLoaderError({
      reason: 'fetch-failed',
      requestUrl: request.url,
      entityId: id,
      routeParams: { modelIdSlug, versionId: versionId ?? undefined },
      error,
    })
    return createModelLoaderSoftFailureResult(request.url)
  }

  // Return a soft-failure shell when we got null back. Anonymous SSR
  // can't see a logged-in owner's private / training-in-progress
  // models; treating that `null` as "model doesn't exist" causes the
  // owner to land on the SSR error page even though the model is
  // perfectly accessible from their browser. The full-SSR entry will
  // mount a bare route-scoped Apollo client and let the client-side
  // hooks try again with `credentials: 'include'`.
  if (!model) {
    log('model loader not found id=%s url=%s', id, request.url)
    await reportModelLoaderError({
      reason: 'primary-data-missing',
      requestUrl: request.url,
      entityId: id,
      routeParams: { modelIdSlug, versionId: versionId ?? undefined },
    })
    return createModelLoaderSoftFailureResult(request.url)
  }

  // Note: we deliberately do NOT re-check `isModelPrivate(model)`
  // here. The upstream API already performs authorization on every
  // `generationModel(id)` query — non-owners receive `null` (handled
  // by the `!model` branch above) while owners receive the full
  // record. Adding a second SSR-side `visibilityType === Private`
  // check would re-block the owner on their own private /
  // training-in-progress LoRA, since the visibility flag is set
  // regardless of caller. Auth-aware SSR (`buildSsrFetchHeaders`
  // forwards the user's `user_token` cookie, see
  // `request-context-als.server.ts`) is what makes that second check
  // unnecessary; before the cookie forwarding shipped, this branch
  // was a hard error path for `private-entity` and is what made
  // `2011612645278020840` render the SSR error page for its owner.

  // If versionId was provided but version doesn't exist, return null
  if (versionId && !modelVersion) {
    log(
      'model loader missing version id=%s versionId=%s url=%s',
      id,
      versionId,
      request.url,
    )
    await reportModelLoaderError({
      reason: 'missing-version',
      requestUrl: request.url,
      entityId: id,
      routeParams: { modelIdSlug, versionId },
    })
    return createModelLoaderErrorResult(request.url, 'missing-version')
  }

  const image = getImageUrl(model.media) || DEFAULT_IMAGE
  // Cover hero <img> on `/model/:idSlug` uses `getPublicUrl(media)` —
  // see `<ModelDetailImage>` -> `<SingleImageViewer originalUrl=...>`.
  // Preload that exact URL so the browser fetches the LCP cover on the
  // critical path. Falls back to `image` (thumbnail/og:image) when the
  // public variant is missing.
  const preloadImage = getPublicUrlFromMedia(model.media) || image

  // Get i18n from middleware context
  const { t } = getI18n(context)
  const translatedTitle = getModelMetaTitleI18n(model, t)
  const fallbackTitle = `${model.title || 'Model'} - AI Art Model | PixAI`
  const title =
    translatedTitle && !translatedTitle.startsWith('undefined')
      ? translatedTitle
      : fallbackTitle
  const description = getModelMetaDescriptionI18n(model, t)

  const authorName =
    model.author?.displayName || model.author?.username || undefined
  const tagNames = (model.tags ?? [])
    .map(tag => tag.name)
    .filter((name): name is string => Boolean(name))
    .slice(0, 5)

  // Build canonical URL first so it can be reused inside the JSON-LD payload.
  const preliminaryMeta = entityMetaLoader({ requestUrl: request.url })
  const canonicalUrl = preliminaryMeta.canonicalUrl

  const jsonLd = buildModelJsonLd({
    model,
    imageUrl: image,
    canonicalUrl,
    description: getModelDescription(model) ?? description,
    authorName,
    tags: tagNames,
  })

  const meta = entityMetaLoader({
    requestUrl: request.url,
    title,
    description,
    image,
    ogType: 'article',
    tags: tagNames,
    author: authorName,
    jsonLd,
    preloadImage,
  })

  return {
    ...meta,
    modelApollo: {
      modelId: id,
      versionId: versionId ?? undefined,
      modelData: {
        generationModel: model,
      },
      versionData: modelVersion
        ? {
            generationModelVersion: modelVersion,
          }
        : undefined,
    },
    modelShell: {
      title: model.title,
      description: getModelDescription(model),
      image,
      authorDisplayName: model.author?.displayName ?? undefined,
      type: model.type,
      versionName:
        modelVersion?.name ?? model.latestAvailableVersion?.name ?? undefined,
      tags: model.tags?.map(tag => tag.name).filter(Boolean) ?? [],
      canonicalUrl: meta.canonicalUrl,
    },
  }
}

/** Meta function for SSR SEO. */
export function meta({ loaderData }: Route.MetaArgs) {
  return entityMeta(loaderData)
}

export const clientLoader =
  createHydratingClientLoader<Awaited<ReturnType<typeof loader>>>(
    getClientModule,
  )

/**
 * Dual-mode entry. `'off'` (CSR) path renders `ModelDetailPage`
 * directly under `<RootRuntime>`'s global Apollo / QueryClient with
 * no SSR seed; `'on'` (Full SSR) path mounts the route-scoped
 * Apollo bridge defined in
 * `clientRoutes/_app.model.$modelIdSlug.($versionId).full-ssr.tsx`.
 *
 * Both entries are top-level imports so `dynamicWithFeatureFlag`
 * stays on the sync render path (no `React.lazy + <Suspense>`, no
 * `B:0` streaming swap). The flag value comes from
 * `useHydratedFeatureFlag` which is server/client consistent, so
 * SSR emits the chosen entry's HTML inline into `<main>` and the
 * dev-mode large-HTML swap-after-paint layout-shift problem is
 * structurally eliminated — there is no outer fallback to flash.
 *
 * `_app.tsx` / `root.tsx` consult `handle.ssrShellExperiment` (see
 * `useResolvedSsrShell`) to decide whether to bypass `<RootRuntime>`
 * for this route, so `'off'` is equivalent to Stage 1 behaviour.
 */
export default dynamicWithFeatureFlag({
  flagName: FeatureFlags.SsrDualMode,
  off: () => <CSREntry />,
  on: () => <FullSSREntry />,
})
